export interface ExtractedProspect {
  name?: string;
  company?: string;
  title?: string;
}

/** Column header patterns that map to prospect fields. */
const CSV_FIELD_MAP: Record<string, keyof ExtractedProspect> = {
  "first name": "name",
  first_name: "name",
  firstname: "name",
  "company name": "company",
  company_name: "company",
  companyname: "company",
  company: "company",
  organization: "company",
  account: "company",
  "account name": "company",
  "job title": "title",
  job_title: "title",
  jobtitle: "title",
  title: "title",
};

/** Headers whose value should be prepended to first name to build full name. */
const LAST_NAME_HEADERS = new Set([
  "last name",
  "last_name",
  "lastname",
  "surname",
]);

/**
 * Parse a CSV row respecting quoted fields (handles commas inside quotes).
 */
function parseCsvRow(row: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (ch === '"') {
      if (inQuotes && row[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      fields.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

/**
 * Extract prospect from a CSV header row + data row.
 * Maps column headers to prospect fields dynamically.
 */
function extractWithHeaders(
  headers: string[],
  values: string[],
): ExtractedProspect | null {
  const headerLower = headers.map((h) => h.trim().toLowerCase());

  const hasKnownHeader = headerLower.some(
    (h) => h in CSV_FIELD_MAP || LAST_NAME_HEADERS.has(h),
  );
  if (!hasKnownHeader) return null;

  const result: ExtractedProspect = {};
  let firstName = "";
  let lastName = "";

  for (let i = 0; i < headerLower.length && i < values.length; i++) {
    const header = headerLower[i] as string;
    const value = (values[i] ?? "").trim();
    if (!value) continue;

    if (LAST_NAME_HEADERS.has(header)) {
      lastName = value;
    } else {
      const field: keyof ExtractedProspect | undefined = CSV_FIELD_MAP[header];
      if (field === "name") {
        firstName = value;
      } else if (field && !result[field]) {
        result[field] = value;
      }
    }
  }

  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  if (fullName) {
    result.name = fullName;
  }

  return Object.keys(result).length > 0 ? result : null;
}

/**
 * ZoomInfo standard CSV column positions (0-indexed).
 * Used when pasting just the data row without headers.
 */
const ZOOMINFO_POSITIONS = {
  lastName: 1,
  firstName: 2,
  jobTitle: 5,
  companyName: 11,
} as const;

/**
 * Try to extract prospect from a single ZoomInfo data row (no headers).
 * Detected when the row starts with a numeric ID and has 50+ comma-separated fields.
 */
function extractZoomInfoDataOnly(values: string[]): ExtractedProspect | null {
  // ZoomInfo rows have 70+ columns and start with a numeric Contact ID
  if (values.length < 30) return null;
  const contactId = (values[0] ?? "").trim();
  if (!/^\d{5,}$/.test(contactId)) return null;

  const get = (idx: number) => (values[idx] ?? "").trim();

  const firstName = get(ZOOMINFO_POSITIONS.firstName);
  const lastName = get(ZOOMINFO_POSITIONS.lastName);
  const jobTitle = get(ZOOMINFO_POSITIONS.jobTitle);
  const companyName = get(ZOOMINFO_POSITIONS.companyName);

  const result: ExtractedProspect = {};
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  if (fullName) result.name = fullName;
  if (companyName) result.company = companyName;
  if (jobTitle) result.title = jobTitle;

  return Object.keys(result).length > 0 ? result : null;
}

/**
 * Try to extract prospect fields from CSV data.
 * Handles two cases:
 *   1. Header row + data row (dynamic column mapping)
 *   2. Data row only (ZoomInfo positional extraction)
 */
function extractFromCsv(lines: string[]): ExtractedProspect | null {
  const firstLine = lines[0];
  if (!firstLine) return null;

  const firstFields = parseCsvRow(firstLine);
  if (firstFields.length < 3) return null;

  const secondLine = lines[1];

  // If there are two lines, try header+data first
  if (secondLine) {
    const secondFields = parseCsvRow(secondLine);
    const headerResult = extractWithHeaders(firstFields, secondFields);
    if (headerResult) return headerResult;
  }

  // Try treating the first line as a headerless ZoomInfo data row
  const dataOnlyResult = extractZoomInfoDataOnly(firstFields);
  if (dataOnlyResult) return dataOnlyResult;

  // If two lines and first didn't work as headers, try second as data-only too
  if (secondLine) {
    const secondFields = parseCsvRow(secondLine);
    const secondDataOnly = extractZoomInfoDataOnly(secondFields);
    if (secondDataOnly) return secondDataOnly;
  }

  return null;
}

/**
 * Extract prospect name, company, and title from pasted CRM/ZoomInfo notes.
 *
 * Supports two formats:
 * 1. Key-value lines: "Name: Bob Smith", "Company: Acme Corp"
 * 2. CSV with header row: ZoomInfo/CRM export with First Name, Last Name, Company Name, etc.
 */
export function extractProspectFromNotes(notes: string): ExtractedProspect {
  const lines = notes
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  // Try CSV extraction first (header + data rows)
  const csvResult = extractFromCsv(lines);
  if (csvResult) return csvResult;

  // Fall back to key-value line matching
  const result: ExtractedProspect = {};

  for (const line of lines) {
    const nameMatch = line.match(
      /^(?:name|contact|prospect|first\s*name|full\s*name)\s*[:=]\s*(.+)/i,
    );
    if (nameMatch?.[1] && !result.name) {
      result.name = nameMatch[1].trim();
      continue;
    }

    const companyMatch = line.match(
      /^(?:company|organization|org|account|employer|business)\s*[:=]\s*(.+)/i,
    );
    if (companyMatch?.[1] && !result.company) {
      result.company = companyMatch[1].trim();
      continue;
    }

    const titleMatch = line.match(
      /^(?:title|role|position|job\s*title)\s*[:=]\s*(.+)/i,
    );
    if (titleMatch?.[1] && !result.title) {
      result.title = titleMatch[1].trim();
    }
  }

  return result;
}
